/**
 * useAuthStore — Zustand store for RBAC permission hydration (Phase 4).
 *
 * Single source of truth for frontend roles/permissions. Populated by a
 * single GET /auth/me call after login. Frontend authorization is UX-only;
 * the backend remains the security source of truth.
 *
 * Architecture rules (non-negotiable):
 *   - Permissions come ONLY from /auth/me — never hardcoded, never from JWT.
 *   - Permissions are NOT stored in localStorage. In-memory only.
 *   - clearSession() is called on logout so stale permissions never linger.
 */

import { create } from 'zustand';
import type {
  AuthSnapshotUser,
  PartnerProfileSnapshot,
  OnboardingSnapshot,
} from './session';
import { fetchAuthSnapshot } from './session';

const DEV_BYPASS = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true' && import.meta.env.MODE !== 'test';

interface AuthStoreState {
  user: AuthSnapshotUser | null;
  roles: string[];
  permissions: string[];
  partnerProfile: PartnerProfileSnapshot | null;
  onboarding: OnboardingSnapshot | null;
  /** True once the first /auth/me round-trip completes (or bypass is active). */
  hydrated: boolean;
  /** True when the user has not yet completed the one-time role selection. */
  roleSelectionRequired: boolean;
  /**
   * True when the user has not accepted the current Terms & User Agreement
   * version — computed server-side (User.termsAcceptedVersion vs.
   * CURRENT_TERMS_VERSION), never from localStorage. Mirrors
   * roleSelectionRequired's shape exactly.
   */
  termsAcceptanceRequired: boolean;

  /**
   * Load (or reload) the RBAC snapshot from /auth/me.
   * Call once after login; safe to call again after profile mutations.
   */
  loadCurrentUser: () => Promise<void>;

  /** Wipe all RBAC state. Call on logout before clearing Firebase session. */
  clearSession: () => void;

  hasPermission: (code: string) => boolean;
  /** True if any PARTNER variant role is held. */
  isPartner: () => boolean;
  isAdmin: () => boolean;
  isAuthenticated: () => boolean;
}

const initialData = {
  user: null,
  roles: [] as string[],
  permissions: [] as string[],
  partnerProfile: null,
  onboarding: null,
  hydrated: false,
  roleSelectionRequired: false,
  termsAcceptanceRequired: false,
} as const;

export const useAuthStore = create<AuthStoreState>((set, get) => ({
  ...initialData,

  loadCurrentUser: async () => {
    if (DEV_BYPASS) {
      // In bypass mode, still call /auth/me so we get the real partner profile
      // and onboarding state from the backend (which also runs in bypass mode).
      try {
        const snapshot = await fetchAuthSnapshot();
        // Merge snapshot with the hardcoded bypass permissions in case /auth/me
        // doesn't include all dev permissions yet.
        set({
          ...snapshot,
          permissions: Array.from(
            new Set([
              ...snapshot.permissions,
              'VIEW_ANALYTICS',
              'MANAGE_PARTNER_PROFILE',
              'MANAGE_INVITATIONS',
              'MANAGE_CLIENT_ACCESS',
              'MANAGE_PARTNER_ATTRIBUTION',
            ]),
          ),
          hydrated: true,
        });
      } catch {
        set({
          user: { id: 'dev-user', email: 'dev@local', primaryRole: 'ADMIN' },
          roles: ['ADMIN'],
          permissions: [
            'VIEW_ANALYTICS',
            'MANAGE_PARTNER_PROFILE',
            'MANAGE_INVITATIONS',
            'MANAGE_CLIENT_ACCESS',
            'MANAGE_PARTNER_ATTRIBUTION',
          ],
          partnerProfile: null,
          onboarding: null,
          hydrated: true,
          // Dev-bypass fallback: keep the modal safely hidden rather than
          // risk blocking local development on a broken /auth/me call.
          termsAcceptanceRequired: false,
        });
      }
      return;
    }
    try {
      const snapshot = await fetchAuthSnapshot();
      set({ ...snapshot, hydrated: true });
    } catch {
      // On failure, mark hydrated (no spinner loop) but keep empty permissions
      // so all permission-gated UI remains safely hidden.
      set({ ...initialData, hydrated: true });
    }
  },

  clearSession: () => set({ ...initialData, hydrated: true }),

  hasPermission: (code: string) => get().permissions.includes(code),

  isPartner: () =>
    get().roles.some(
      (r) => r === 'PARTNER' || r === 'PARTNER_ADMIN' || r === 'PARTNER_STAFF',
    ),

  isAdmin: () => get().roles.includes('ADMIN'),

  isAuthenticated: () => get().user !== null,
}));
